(() => {
  if (window.ProductSwatches) return;

  const SEARCH_VIEW = 'swatch-products';
  const TITLE_BATCH_SIZE = 4;
  const CACHE_TTL_MS = 10 * 60 * 1000;
  const SESSION_CACHE_KEY = 'product-swatches:families:v1';

  const familyCache = new Map();
  const inFlightByTitle = new Map();

  const normalize = (value) => {
    return String(value || '').trim().toLowerCase();
  };

  const normalizeTitleKey = (value) => {
    return normalize(value);
  };

  const parseCsv = (value) => {
    return String(value || '')
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);
  };

  const getPixelSize = (value, fallback) => {
    const parsed = Number.parseInt(String(value || ''), 10);

    if (!Number.isFinite(parsed)) {
      return fallback;
    }

    return Math.max(12, Math.min(parsed, 138));
  };

  const chunk = (items, size) => {
    const chunks = [];

    for (let index = 0; index < items.length; index += size) {
      chunks.push(items.slice(index, index + size));
    }

    return chunks;
  };

  const uniqueTitles = (titles) => {
    const seen = new Set();
    const result = [];

    (titles || []).forEach((value) => {
      const title = String(value || '').trim();
      const key = normalizeTitleKey(title);

      if (!title || seen.has(key)) return;

      seen.add(key);
      result.push(title);
    });

    return result;
  };

  const escapeSearchValue = (value) => {
    return String(value || '')
      .replace(/\\/g, '\\\\')
      .replace(/"/g, '\\"');
  };

  const getSearchUrl = () => {
    const root =
      window.Shopify &&
      window.Shopify.routes &&
      window.Shopify.routes.root
        ? window.Shopify.routes.root
        : '/';

    return new URL(`${root}search`, window.location.origin);
  };

  const sanitizeProduct = (product) => {
    return {
      id: String(product?.id || ''),
      title: String(product?.title || '').trim(),
      handle: String(product?.handle || '').trim(),
      url: String(product?.url || '').trim(),
      vendor: String(product?.vendor || '').trim(),
      available: Boolean(product?.available)
    };
  };

  const readSessionCache = () => {
    try {
      const raw = window.sessionStorage.getItem(
        SESSION_CACHE_KEY
      );

      if (!raw) return;

      const parsed = JSON.parse(raw);
      const now = Date.now();

      Object.entries(parsed || {}).forEach(
        ([key, entry]) => {
          if (
            !entry ||
            !Array.isArray(entry.products) ||
            !Number.isFinite(entry.expiresAt) ||
            entry.expiresAt <= now
          ) {
            return;
          }

          familyCache.set(key, {
            title: String(entry.title || ''),
            products: entry.products.map(
              sanitizeProduct
            ),
            expiresAt: entry.expiresAt
          });
        }
      );
    } catch (error) {
      // Storage can be unavailable in some browsing modes.
    }
  };

  const writeSessionCache = () => {
    try {
      const payload = {};
      const now = Date.now();

      familyCache.forEach((entry, key) => {
        if (!entry || entry.expiresAt <= now) return;

        payload[key] = entry;
      });

      window.sessionStorage.setItem(
        SESSION_CACHE_KEY,
        JSON.stringify(payload)
      );
    } catch (error) {
      // In-memory caching still works if sessionStorage fails.
    }
  };

  const getCachedFamily = (title) => {
    const key = normalizeTitleKey(title);
    const entry = familyCache.get(key);

    if (!entry) return null;

    if (entry.expiresAt <= Date.now()) {
      familyCache.delete(key);
      writeSessionCache();
      return null;
    }

    return entry.products;
  };

  const setCachedFamily = (title, products) => {
    const cleanTitle = String(title || '').trim();
    const key = normalizeTitleKey(cleanTitle);

    if (!key) return;

    familyCache.set(key, {
      title: cleanTitle,
      products: (products || []).map(
        sanitizeProduct
      ),
      expiresAt: Date.now() + CACHE_TTL_MS
    });
  };

  const buildTitleQuery = (titles) => {
    return titles
      .map(
        (title) =>
          `title:"${escapeSearchValue(title)}"`
      )
      .join(' OR ');
  };

  const fetchSearchPage = async (
    titles,
    page = 1
  ) => {
    const url = getSearchUrl();

    url.searchParams.set(
      'q',
      buildTitleQuery(titles)
    );

    url.searchParams.set(
      'type',
      'product'
    );

    url.searchParams.set(
      'view',
      SEARCH_VIEW
    );

    // We need sold-out sibling products in the family data.
    url.searchParams.set(
      'options[unavailable_products]',
      'show'
    );

    // Exact family matching is enforced again in JS, but
    // disabling prefix matching avoids unnecessary candidates.
    url.searchParams.set(
      'options[prefix]',
      'none'
    );

    if (page > 1) {
      url.searchParams.set(
        'page',
        String(page)
      );
    }

    const response = await fetch(
      url.toString(),
      {
        method: 'GET',
        headers: {
          Accept: 'application/json'
        },
        credentials: 'same-origin'
      }
    );

    if (!response.ok) {
      throw new Error(
        `Product swatch lookup failed with ${response.status}`
      );
    }

    const text = await response.text();

    try {
      return JSON.parse(text);
    } catch (error) {
      console.error(
        'Product swatch search response was not valid JSON:',
        text
      );

      throw error;
    }
  };

  const fetchAllSearchPages = async (titles) => {
    const results = [];
    let page = 1;
    let hasNext = true;

    // Defensive guard. Shopify's Liquid search pagination is
    // 50 results/page; a product family should never approach
    // 10 pages in this implementation.
    const maxPages = 10;

    while (hasNext && page <= maxPages) {
      const data = await fetchSearchPage(
        titles,
        page
      );

      if (Array.isArray(data?.results)) {
        results.push(...data.results);
      }

      hasNext = Boolean(data?.has_next);
      page += 1;
    }

    return results;
  };

  const fetchAndCacheBatch = async (titles) => {
    const requestedTitles = uniqueTitles(titles);

    if (!requestedTitles.length) {
      return;
    }

    const products = await fetchAllSearchPages(
      requestedTitles
    );

    const productsByTitle = new Map(
      requestedTitles.map(
        (title) => [title, []]
      )
    );

    const seenIdsByTitle = new Map(
      requestedTitles.map(
        (title) => [title, new Set()]
      )
    );

    products.forEach((rawProduct) => {
      const product = sanitizeProduct(rawProduct);

      // The Shopify search query finds candidates.
      // Exact title equality is the authoritative family rule.
      const requestedTitle =
        requestedTitles.find(
          (title) =>
            normalizeTitleKey(title) ===
            normalizeTitleKey(product.title)
        );

      if (!requestedTitle) return;

      const seenIds =
        seenIdsByTitle.get(requestedTitle);

      if (
        product.id &&
        seenIds.has(product.id)
      ) {
        return;
      }

      if (product.id) {
        seenIds.add(product.id);
      }

      productsByTitle
        .get(requestedTitle)
        .push(product);
    });

    requestedTitles.forEach((title) => {
      setCachedFamily(
        title,
        productsByTitle.get(title) || []
      );
    });

    writeSessionCache();
  };

  const startMissingTitleRequests = (
    titles
  ) => {
    const missingTitles = uniqueTitles(titles)
      .filter((title) => {
        const key = normalizeTitleKey(title);

        return (
          getCachedFamily(title) === null &&
          !inFlightByTitle.has(key)
        );
      });

    chunk(
      missingTitles,
      TITLE_BATCH_SIZE
    ).forEach((batch) => {
      const batchPromise =
        fetchAndCacheBatch(batch);

      batch.forEach((title) => {
        const key = normalizeTitleKey(title);

        const titlePromise = batchPromise
          .then(() => {
            return (
              getCachedFamily(title) || []
            );
          })
          .finally(() => {
            inFlightByTitle.delete(key);
          });

        inFlightByTitle.set(
          key,
          titlePromise
        );
      });
    });
  };

  const getPartnerProductsForTitles = async (
    titles
  ) => {
    const requestedTitles =
      uniqueTitles(titles);

    if (!requestedTitles.length) {
      return new Map();
    }

    startMissingTitleRequests(
      requestedTitles
    );

    await Promise.all(
      requestedTitles.map((title) => {
        const cached =
          getCachedFamily(title);

        if (cached !== null) {
          return Promise.resolve(cached);
        }

        const inFlight =
          inFlightByTitle.get(
            normalizeTitleKey(title)
          );

        return inFlight || Promise.resolve([]);
      })
    );

    return new Map(
      requestedTitles.map((title) => [
        title,
        getCachedFamily(title) || []
      ])
    );
  };

  const getPartnerProducts = async (title) => {
    const cleanTitle =
      String(title || '').trim();

    if (!cleanTitle) return [];

    const cached =
      getCachedFamily(cleanTitle);

    if (cached !== null) {
      return cached;
    }

    const families =
      await getPartnerProductsForTitles([
        cleanTitle
      ]);

    return (
      families.get(cleanTitle) || []
    );
  };

  const productRuleApplies = (
    swatch,
    productId = '',
    productHandle = ''
  ) => {
    const productRule =
      swatch.productRule || 'exclude';

    const productIds =
      swatch.productIds || [];

    const productHandles =
      swatch.productHandles || [];

    const hasSelectedProducts = Boolean(
      productIds.length ||
      productHandles.length
    );

    const matchesProduct =
      productIds.some(
        (id) =>
          String(id) ===
          String(productId)
      ) ||
      productHandles.some(
        (handle) =>
          normalize(handle) ===
          normalize(productHandle)
      );

    if (productRule === 'include') {
      return (
        hasSelectedProducts &&
        matchesProduct
      );
    }

    return !matchesProduct;
  };

  const getConfiguredGroups = ({
    productId = '',
    productHandle = ''
  } = {}) => {
    const section =
      document.querySelector(
        '[data-product-variant-swatches]'
      );

    if (!section) return [];

    return [
      ...section.querySelectorAll(
        '[data-product-swatch-group]'
      )
    ].map((groupEl) => {
      const swatches = [
        ...groupEl.querySelectorAll(
          '[data-product-swatch]'
        )
      ]
        .map((swatchEl) => {
          const swatch = {
            label: String(
              swatchEl.getAttribute(
                'data-option-value'
              ) || ''
            ).trim(),
            image:
              swatchEl.getAttribute(
                'data-swatch-image'
              ) || '',
            style:
              swatchEl.getAttribute(
                'data-swatch-style'
              ) || 'line',
            productRule:
              swatchEl.getAttribute(
                'data-product-rule'
              ) || 'exclude',
            productIds: parseCsv(
              swatchEl.getAttribute(
                'data-product-ids'
              )
            ),
            productHandles: parseCsv(
              swatchEl.getAttribute(
                'data-product-handles'
              )
            ),
            colors: [
              swatchEl.getAttribute(
                'data-swatch-color-1'
              ),
              swatchEl.getAttribute(
                'data-swatch-color-2'
              ),
              swatchEl.getAttribute(
                'data-swatch-color-3'
              ),
              swatchEl.getAttribute(
                'data-swatch-color-4'
              ),
              swatchEl.getAttribute(
                'data-swatch-color-5'
              )
            ].filter(Boolean)
          };

          return swatch;
        })
        .filter((swatch) => {
          if (
            !swatch.label ||
            (
              !swatch.image &&
              !swatch.colors.length
            )
          ) {
            return false;
          }

          return productRuleApplies(
            swatch,
            productId,
            productHandle
          );
        });

      return {
        valueSource:
          groupEl.getAttribute(
            'data-value-source'
          ) || 'variant_option',
        optionName:
          groupEl.getAttribute(
            'data-option-name'
          ) || '',
        productSwatchSize:
          getPixelSize(
            groupEl.getAttribute(
              'data-product-swatch-size'
            ),
            40
          ),
        collectionSwatchSize:
          getPixelSize(
            groupEl.getAttribute(
              'data-collection-swatch-size'
            ),
            34
          ),
        rolloverPreview:
          groupEl.getAttribute(
            'data-rollover-preview'
          ) === 'true',
        labelPopup:
          groupEl.getAttribute(
            'data-label-popup'
          ) === 'true',
        swatchPreview:
          groupEl.getAttribute(
            'data-swatch-preview'
          ) === 'true',
        showOnCard:
          groupEl.getAttribute(
            'data-show-on-card'
          ) === 'true',
        swatches
      };
    });
  };

  const getSwatchForValue = (
    group,
    value
  ) => {
    if (!group) return null;

    return (
      (group.swatches || []).find(
        (swatch) =>
          normalize(swatch.label) ===
          normalize(value)
      ) || null
    );
  };

  const clearCache = () => {
    familyCache.clear();
    inFlightByTitle.clear();

    try {
      window.sessionStorage.removeItem(
        SESSION_CACHE_KEY
      );
    } catch (error) {
      // Ignore storage failures.
    }
  };

  const debugCache = () => {
    return new Map(familyCache);
  };

  readSessionCache();

  window.ProductSwatches = {
    normalize,
    getConfiguredGroups,
    getSwatchForValue,
    getPartnerProducts,
    getPartnerProductsForTitles,
    clearCache,
    debugCache
  };
})();
